/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { array, arrayOf, bool, func, number, object, oneOf, string, node } from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import PointCross from 'yoda-core-components/lib/components/PointCross/PointCross';
import Rating from 'yoda-core-components/lib/components/Rating/rating';
import S7Image from 'yoda-core-components/lib/components/S7Image/S7Image';
import {
    getURLParameterBykey,
    updateQueryStringParameter,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    isDesktopWidth,
    isMobileWidth,
    desktopQuery,
} from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import ErrorBoundaryConnected from '../ErrorBoundary/ErrorBoundary';
import QuickAddToCartConnected from '../QuickAddTocart/QuickAddToCart';
import SaveForLaterConnected from '../SaveForLater/SaveForLater';
import SlickCarousel from '../SlickCarousel/SlickCarousel';
import * as styles from './ProductCard.css';
import { cardThemes, cardTypes, LAZY_LOAD_SCROLL_OFFSET } from './ProductCard.config';
import EcoRebates from './components/EcoRebates/EcoRebates';
import ExtendedSize from './components/ExtendedSize/ExtendedSize';
import ProductAvailabilityConnected from './components/ProductAvailability/ProductAvailability';
import ProductBadge from './components/ProductBadge/ProductBadge';
import ProductImage from './components/ProductImage/ProductImage';
import ProductPrice from './components/ProductPrice/ProductPrice';
import ProductTitle from './components/ProductTitle/ProductTitle';
import ProductVideo from './components/ProductVideo/ProductVideo';
import Swatches from './components/Swatches';
import {
    selectFeatureFlags,
    selectPreferencesNoImageFoundSrc,
} from '../../selectors/ContextSelector';
import SwatchesSkeleton from './components/Swatches/SwatchesSkeleton';
import { selectStoreAvailabilityFilter } from '../../selectors/filtersSelector';

const cx = classNames.bind(styles);

const ProductCard = ({
    altImageUrl,
    badge,
    bottomBadge,
    cardCustomClass,
    cardTheme,
    cardType,
    customClass,
    defaultOffSet,
    ecoRebate,
    enableMiniPDP,
    enableProductTitlePrefix,
    imageUrl,
    index,
    isAddToCartClick,
    isCouponRecommendationsModal,
    isCriteoProduct,
    isEnsembledProduct,
    lazyLoadImages,
    noImageFoundSrcDefault,
    onAddToCartClick,
    onImageClick,
    onProductClick,
    ppId,
    pricingConfig,
    productUrl,
    shallowCard,
    showPaneLoader,
    showPricing,
    showSaveForlater,
    skuId,
    skuSwatch,
    title,
    upperBadge,
    videoId,
    enableGridViewInCertona,
    displayScene7PictureComponent,
    displayS7PictureComponentAltImg,
    fromHotDeals,
    availabilityStatus,
    galleryProductCard,
    triggerAnalytics,
    displayEach,
    eligibilityMessage,
    price,
    rating,
    reviewCount,
    lazyLoadSwatches,
    deviceType,
    deviceType: { isDesktop },
    children,
    setImageHeight,
    isproductList,
}) => {
    const storeAvailabilityFilterData = useSelector(selectStoreAvailabilityFilter);

    const {
        lazyLoadNextGalleryCards = false,
        disableEnsembledImage = false,
        enableAddToCartForBOPIS = false,
        hideBottomBadge = false,
        hideTopLeftBadge = false,
        enableSaveForLater,
        enableSmoothScrollIOSGallerySearch = false,
    } = useSelector(selectFeatureFlags);

    const { deviceType: { isBot = false } = {} } = useSelector(({ context }) => context);

    const noImageFoundSrc = useSelector(selectPreferencesNoImageFoundSrc);

    const productCardRef = useRef();

    const [imageData, setImageData] = useState({
        imageError: false,
        imageSrc: '',
        swatchImageSrc: '',
        swatchSkuId: null,
        activeSwatchImageId: '',
        altImageUrl,
        swatchClicked: false,
    });
    const [defaultSwatchData, setDefaultSwatchData] = useState(null);
    const [playVideo, setPlayVideo] = useState(false);
    const [enableProductSliding, setEnableProductSliding] = useState(() => !isDesktopWidth());
    const [imageLoad, setImageLoad] = useState(false);

    /* istanbul ignore next */
    const handleResize = (event) => {
        setEnableProductSliding(!event?.matches);
    };

    useEffect(() => {
        const query = window.matchMedia(desktopQuery);
        /* istanbul ignore next */
        query.onchange = (e) => handleResize(e);
    }, []);

    const lazyLoadGalleryCardsNumber = 8;
    const scene7smallScreenOffset = 170;
    const scene7BigScreenOffset = 350;
    let scene7smallScreenOffsetURL = '';
    let scene7BigScreenOffsetURL = '';
    let scene7smallScreenOffsetAltURL = '';
    let scene7BigScreenOffsetAltURL = '';

    const lazyLoadGalleryCardsScrollOffset =
        defaultOffSet > LAZY_LOAD_SCROLL_OFFSET[lazyLoadGalleryCardsNumber]
            ? defaultOffSet
            : LAZY_LOAD_SCROLL_OFFSET[lazyLoadGalleryCardsNumber];

    const pointCrossOffsetProps = lazyLoadNextGalleryCards
        ? { offset: lazyLoadGalleryCardsScrollOffset }
        : {};

    const onImageLoad = () => {
        if (!imageLoad) {
            setImageLoad(true);
        }
    };

    /* istanbul ignore next */
    const handleSwatchDesktopEvent = useCallback(
        (event, currentSwatch) => {
            event.preventDefault();
            const getScene7ImageUrl = (imageId) =>
                (imageId &&
                    `//jcpenney.scene7.com/is/image/JCPenney/${imageId}?wid=350&hei=350&op_usm=.4,.8,0,0&resmode=sharp2`) ||
                '';

            if (currentSwatch?.skuId) {
                // TOFP2P-2033: stop video playing when swatch is selected
                setPlayVideo(false);

                const {
                    colorizedImageId = '',
                    skuAltImages: [{ imgId = '' } = {}] = [],
                } = currentSwatch;
                const altImgUrl = getScene7ImageUrl(imgId);
                const imgUrl = getScene7ImageUrl(colorizedImageId);

                const newImageData = {
                    imageError: false,
                    imageSrc: imgUrl,
                    swatchImageSrc: imgUrl,
                    swatchSkuId: currentSwatch?.skuId,
                };

                if (!disableEnsembledImage)
                    newImageData.altImageUrl = isEnsembledProduct ? altImgUrl || imgUrl : altImgUrl;

                if (event?.type === 'click') {
                    newImageData.activeSwatchImageId = currentSwatch?.swatchImageId;
                    newImageData.swatchClicked = true;
                    setDefaultSwatchData({ ...newImageData });
                }

                setImageData((prevImageData) => {
                    return { ...prevImageData, ...newImageData };
                });
            }

            if (event?.type === 'mouseleave' && !imageData?.swatchClicked) {
                setImageData((prevImageData) => {
                    return {
                        ...prevImageData,
                        imageSrc: '',
                        swatchImageSrc: '',
                        swatchSkuId: null,
                        activeSwatchImageId: '',
                        altImageUrl,
                    };
                });
            }
        },
        [imageData]
    );

    /* istanbul ignore next */
    const setGalleryScrollInLocalStorage = useCallback(() => {
        const topOfElement = !__SERVER__ ? window.pageYOffset : 1000;
        LocalStorage.setData('galleryScrollTo', ppId);
        LocalStorage.setData('galleryScrollToOffSet', topOfElement);
    }, [ppId]);

    /* istanbul ignore next */
    const getFacetSelectedValue = (url) => {
        const facetSelected = getURLParameterBykey('facetSelected', url);

        if (facetSelected?.length === 0) {
            return 'color';
        }
        const hasColorFacet = facetSelected?.split(',')?.includes('color');
        return hasColorFacet ? facetSelected : `${facetSelected},color`;
    };

    // for Mobile and Tablet redirect to PDP
    /* istanbul ignore next */
    // eslint-disable-next-line no-shadow
    const handleSwatchMobileEvent = useCallback(
        ({ skuId }) => {
            if (skuId && productUrl) {
                const updatedProductUrl = updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    skuId
                );

                LocalStorage.setData('fromGallery', { scrollTo: 'swatch' });
                setGalleryScrollInLocalStorage();
                window.location.assign(
                    updateQueryStringParameter(
                        updatedProductUrl,
                        'facetSelected',
                        getFacetSelectedValue(updatedProductUrl)
                    )
                );
            }
        },
        [productUrl]
    );

    const handleVideoChange = (newValue) => {
        setPlayVideo(newValue);
    };

    const getProductVideo = (additionalProps) => {
        return videoId ? (
            <ProductVideo
                videoId={videoId}
                index={index}
                galleryProductCard={galleryProductCard}
                triggerAnalytics={triggerAnalytics}
                playVideo={playVideo}
                setParentState={handleVideoChange}
                {...additionalProps}
            />
        ) : null;
    };
    const getExtendedSize = (additionalProps) => {
        return bottomBadge?.text ? (
            <ExtendedSize bottomBadge={bottomBadge} cardTheme={cardTheme} {...additionalProps} />
        ) : null;
    };

    const getProductBadge = useCallback(() => {
        const activeBadge = badge || upperBadge;
        return activeBadge?.text ? (
            <ProductBadge badge={activeBadge} playVideo={playVideo} />
        ) : null;
    }, [badge, upperBadge, playVideo]);

    /**
     * display default image for ensembled products
     * when user hovers out of img/alt-image/swatches
     */
    /* istanbul ignore next */
    const resetSelectionForEnsembleHover = isproductList
        ? useCallback(() => {
              if (isEnsembledProduct) {
                  const newImageData = {
                      swatchImageSrc: '',
                      imageSrc: '',
                      imageError: false,
                      swatchSkuId: '',
                      activeSwatchImageId: '',
                  };
                  setImageData(defaultSwatchData || newImageData);
              }
          }, [isEnsembledProduct])
        : () => {};

    const constructProductImagesAndVideos = () => {
        const productImagesAndVideos = [];

        const formatImageUrls = (url, offset, urlFormatCheck) => {
            return `${url}${urlFormatCheck ? '&' : '?'}wid=${offset}&hei=${offset}&op_sharpen=1`;
        };
        const isdisplayUrlFormat = (url) => url.indexOf('?') > 0;
        const matchImage = (url) => /scene7.com(.+)/g.test(url);

        if (imageUrl) {
            let displayUrl = imageUrl;
            if (matchImage(imageUrl) && displayScene7PictureComponent) {
                scene7smallScreenOffsetURL = formatImageUrls(
                    imageUrl,
                    scene7smallScreenOffset,
                    isdisplayUrlFormat
                );
                scene7BigScreenOffsetURL = formatImageUrls(
                    imageUrl,
                    scene7BigScreenOffset,
                    isdisplayUrlFormat
                );
                if (displayS7PictureComponentAltImg) {
                    displayUrl = isMobileWidth()
                        ? scene7smallScreenOffsetURL
                        : scene7BigScreenOffsetURL;
                }
            } else if (matchImage(imageUrl)) {
                displayUrl = `${displayUrl}${
                    isdisplayUrlFormat(imageUrl) ? '&' : '?'
                }wid=170&hei=170&op_sharpen=1`;
            }
            const imageobj = {
                altText: title,
                displayText: title,
                key: displayUrl,
                type: 'PRIMARY',
                url: displayUrl,
            };

            productImagesAndVideos.push(imageobj);
        }

        if (altImageUrl) {
            let displayAltImgUrl = altImageUrl;
            /* istanbul ignore next */
            if (matchImage(altImageUrl) && displayS7PictureComponentAltImg) {
                scene7smallScreenOffsetAltURL = formatImageUrls(
                    altImageUrl,
                    scene7smallScreenOffset,
                    displayAltImgUrl
                );
                scene7BigScreenOffsetAltURL = formatImageUrls(
                    altImageUrl,
                    scene7BigScreenOffset,
                    displayAltImgUrl
                );
                displayAltImgUrl = isMobileWidth()
                    ? scene7smallScreenOffsetAltURL
                    : scene7BigScreenOffsetAltURL;
            } else if (matchImage(altImageUrl)) {
                displayAltImgUrl = `${displayAltImgUrl}${
                    isdisplayUrlFormat(altImageUrl) ? '&' : '?'
                }wid=170&hei=170&op_sharpen=1`;
            }
            const altImageobj = {
                altText: title,
                key: displayAltImgUrl,
                swatchUrl: displayAltImgUrl,
                type: 'ALTERNATE',
                url: displayAltImgUrl,
            };

            productImagesAndVideos.push(altImageobj);
        }

        return productImagesAndVideos;
    };

    const renderProductImage = () => {
        const swatchAltImage = imageData?.altImageUrl;
        const { imageSrc, swatchImageSrc, swatchSkuId } = imageData;

        const propsClone = {
            cardTheme,
            imageUrl,
            lazyLoadImages,
            onProductClick,
            title,
            productUrl,
        };

        /* istanbul ignore next */
        if (swatchSkuId) {
            const productUrlUpdated = updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

            const productUpdateFacetUrl = updateQueryStringParameter(
                productUrlUpdated,
                'facetSelected',
                getFacetSelectedValue(productUrlUpdated)
            );

            propsClone.productUrl = productUpdateFacetUrl;
        }
        const altImgUrl = swatchAltImage;
        const enableProductImage = !enableProductSliding || cardTheme !== cardThemes?.GALLERY;

        const productImageProps = {
            ...propsClone,
            altImageUrl: altImgUrl,
            imageSrc,
            pointCrossOffsetProps,
            swatchImageSrc,
            onLoad: onImageLoad(),
        };

        return enableProductImage && <ProductImage {...productImageProps} />;
    };

    const renderProductSliding = () => {
        // all below changes are for SlickCarousel
        const productImagesAndVideos = galleryProductCard && constructProductImagesAndVideos();
        const productImageAndVideosLength = productImagesAndVideos?.length;

        // clean up styles
        const carouselTheme = {
            carouselSelected: cx('selected'),
            carouselBullets: cx('carouselBullets'),
            bullet: cx('bullet'),
            slideClass: cx('carouselSlidecx'),
            bulletContainer: cx('bulletContainer'),
            carouselContainer: galleryProductCard ? cx('carouselContainerGallery') : '',
        };
        const key = `${productImageAndVideosLength}-s`;
        const displayProductSliding = enableProductSliding && productImageAndVideosLength > 0; // as product card will be common component added to make sure it will load only for gallery not for RR zone
        const carouselThemeGallery = {
            bulletContainer: `${dt([
                'absolute',
                'bottom-1',
                'w-full',
                'bg-transparent',
                'align-top',
            ])}`,
            bullet: `${dt(['align-top', 'ml-2', 'mr-0', 'border-0'])}`,
        };

        const productImageListRenderer = (dataItem, idx) => {
            const { url = '', altText = '' } = dataItem;
            const noImageSrc = noImageFoundSrc || noImageFoundSrcDefault;

            let productUpdatedUrl = productUrl;
            const { swatchSkuId } = imageData;

            /* istanbul ignore next */
            if (swatchSkuId) {
                productUpdatedUrl = updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    swatchSkuId
                );
            }

            /* istanbul ignore next */
            const pictureimgUrl = isMobileWidth()
                ? scene7smallScreenOffsetURL
                : scene7BigScreenOffsetURL;
            const image = (
                <a
                    href={productUpdatedUrl}
                    onClick={(e) => {
                        onImageClick(e, 'image');
                    }}
                >
                    <ErrorBoundaryConnected>
                        {displayScene7PictureComponent ? (
                            // eslint-disable-next-line react/no-unknown-property
                            <picture automationId="at-responsive-component-image">
                                {/* small/mobile breakpoint */}
                                <source
                                    media="(max-width: 767px)"
                                    srcSet={
                                        displayS7PictureComponentAltImg
                                            ? url
                                            : scene7smallScreenOffsetURL
                                    }
                                />
                                {/* large/desktop breakpoint */}
                                <S7Image
                                    src={displayS7PictureComponentAltImg ? url : pictureimgUrl}
                                    alt={altText}
                                    automationId={`slick-1-s7image-${idx}`}
                                    imageErrorSrc={noImageSrc}
                                    renderFromPictureComponent
                                    onLoad={onImageLoad()}
                                />
                            </picture>
                        ) : (
                            <S7Image
                                src={url}
                                alt={altText}
                                automationId={`slick-1-s7image-${idx}`}
                                imageErrorSrc={noImageSrc}
                                onLoad={onImageLoad()}
                            />
                        )}
                    </ErrorBoundaryConnected>
                </a>
            );

            return lazyLoadImages ? (
                <PointCross
                    lazyLoadComponent
                    fromGallery
                    {...pointCrossOffsetProps}
                    loader={
                        galleryProductCard ? (
                            <div
                                className={dt([
                                    'sm:min-h-[150px]',
                                    'md:min-h-[235px]',
                                    'lg:min-h-[300px]',
                                ])}
                            />
                        ) : null
                    }
                >
                    {image}
                </PointCross>
            ) : (
                image
            );
        };

        return displayProductSliding ? (
            <ErrorBoundaryConnected>
                <SlickCarousel
                    isbotWrapperClass={isBot && galleryProductCard ? 'isbotWrapperClass' : ''}
                    carouselData={productImagesAndVideos}
                    carouselItemRenderer={productImageListRenderer}
                    themeConfig={carouselTheme}
                    automationId="data-slick-carousel"
                    rewindOnChange
                    autoSize
                    lazyLoad
                    displayArrows={false}
                    repeat={false}
                    carouselBulletTheme={carouselThemeGallery}
                    key={key}
                />
            </ErrorBoundaryConnected>
        ) : null;
    };

    const renderPricing = useMemo(() => {
        return showPricing ? (
            <ProductPrice
                {...{
                    cardTheme,
                    deviceType,
                    displayEach,
                    eligibilityMessage,
                    ppId,
                    price,
                    pricingConfig,
                    fromHotDeals,
                }}
            />
        ) : null;
    }, [
        showPricing,
        cardTheme,
        deviceType,
        displayEach,
        eligibilityMessage,
        ppId,
        price,
        pricingConfig,
        fromHotDeals,
    ]);

    const renderRating = useMemo(() => {
        const config = {
            anchor: {
                href: productUrl,
                onClick: (event) => {
                    onProductClick(event, 'reviews');
                },
            },
            rating: {
                automationId: 'productCard-automation-rating',
                averageRating: rating,
                className: cardTheme,
                ratingIconSize: 'small',
                totalRatings:
                    typeof reviewCount === 'string' ? parseInt(reviewCount, 10) : reviewCount,
                wrapperClassName: dt(['mt-1', 'sm:!leading-6']),
                showCount: true,
            },
        };

        return productUrl ? (
            <a {...config?.anchor}>
                <Rating {...config?.rating} />
            </a>
        ) : (
            <Rating {...config?.rating} />
        );
    }, [productUrl, rating, cardTheme, reviewCount]);

    const renderCard = () => {
        const { text = '' } = upperBadge;
        const { activeSwatchImageId, swatchSkuId } = imageData;
        const propsClone = { productUrl };
        const titlePrefix =
            enableProductTitlePrefix && title && text?.toLowerCase().indexOf('new') > -1
                ? 'New!'
                : '';

        if (swatchSkuId)
            propsClone.productUrl = updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

        // Adding a check to stop sending skuId to quickAddToCart as there was some issue with pre-selection of options in mini-PDP.
        const quickAddToCartSkuId = galleryProductCard ? '' : skuId;
        const saveForLaterDisplay = isproductList
            ? useMemo(() => {
                  if (showSaveForlater && enableSaveForLater) {
                      const isFirstProduct = index === 1;
                      return (
                          <div className={cx('saveForLaterContainer')}>
                              <SaveForLaterConnected
                                  cardType={cardType}
                                  isFirstProduct={isFirstProduct}
                                  ppId={ppId}
                                  skuId={skuId}
                              />
                          </div>
                      );
                  }
                  return null;
              }, [showSaveForlater, cardType, ppId, skuId])
            : null;

        const renderProductTitle = useMemo(() => {
            return (
                <ProductTitle
                    prefix={titlePrefix}
                    onProductClick={onProductClick}
                    title={title}
                    productUrl={propsClone?.productUrl}
                />
            );
        }, [title, propsClone?.productUrl]);

        const renderSkeletonLoader = () => (
            <div
                className={`${dt([
                    'absolute',
                    'top-0',
                    'h-full',
                    'w-full',
                    'bg-light-white',
                    'overflow-hidden',
                ])} ${cx('imageLoading')}`}
            />
        );
        const { disableFPACAPI } = pricingConfig;
        const enableNewPriceFormat = disableFPACAPI === false;

        const isStoreAvailable = storeAvailabilityFilterData?.length
            ? !!(
                  storeAvailabilityFilterData?.length &&
                  storeAvailabilityFilterData[0]?.facetValueList?.length
              )
            : false;

        const facetValueList = isStoreAvailable
            ? storeAvailabilityFilterData[0].facetValueList
            : [];

        /* returns if SDPU filter is enabled in gallery */
        let { selected } =
            facetValueList.find(
                (facetConfig) => facetConfig?.facetValId === 832 && !facetConfig?.greyOut
            ) || {};

        /* should always return true is flag is enabled */
        if (enableAddToCartForBOPIS) {
            selected = false;
        }

        const renderQuickAddToCart = isproductList
            ? useMemo(() => {
                  return (
                      <QuickAddToCartConnected
                          ppId={ppId}
                          skuId={quickAddToCartSkuId}
                          automationId="QuickAddToCart"
                          themeConfig={{
                              buttonCustomClass: cx('quickAddToCart', 'iconButtonCart'),
                              wrapperClass: cx('quickAddToCartWrapper', 'iconCartWrapper'),
                          }}
                          location={cardTheme}
                          url={
                              swatchSkuId
                                  ? updateQueryStringParameter(
                                        productUrl,
                                        'selectedSKUId',
                                        swatchSkuId
                                    )
                                  : productUrl
                          }
                          iconStyle
                          enableMiniPDP={enableMiniPDP && !selected}
                          onAddToCartClick={onAddToCartClick}
                          isAddToCartClick={isAddToCartClick}
                          gallerySmoothScroll={enableSmoothScrollIOSGallerySearch}
                      />
                  );
              }, [selected])
            : null;

        const renderSwatches = isproductList
            ? useCallback(() => {
                  if (skuSwatch?.length) {
                      const swatchComponent = () => (
                          <Swatches
                              {...{
                                  activeSwatchImageId,
                                  handleSwatchDesktopEvent,
                                  handleSwatchMobileEvent,
                                  isEnsembledProduct,
                                  productCardRef,
                                  productUrl,
                                  setGalleryScrollInLocalStorage,
                                  skuSwatch,
                                  index,
                                  desktopWidth: !enableProductSliding,
                              }}
                          />
                      );
                      return (
                          // TODO: MNPDPYODA-12118 removed lazyload - this needs to be refactored
                          lazyLoadSwatches ? (
                              <PointCross
                                  lazyLoadComponent
                                  {...pointCrossOffsetProps}
                                  loader={galleryProductCard ? <SwatchesSkeleton /> : null}
                              >
                                  {swatchComponent()}
                              </PointCross>
                          ) : (
                              swatchComponent()
                          )
                      );
                  }

                  return null;
              }, [skuSwatch, lazyLoadSwatches, activeSwatchImageId, enableProductSliding])
            : () => null;

        return (
            <div
                className={cx('container', cardType, {
                    miniPDPEnabled: enableMiniPDP,
                })}
            >
                <div
                    ref={(elm) => {
                        setImageHeight && setImageHeight(elm);
                    }}
                    className={cx('imgBlock', {
                        catridgeImgBlock: enableNewPriceFormat,
                        galleryVideoImgBlock: galleryProductCard && videoId,
                    })}
                >
                    {/* With the enablePrice Feature Flag 'catridgeImgBlock' have to append */}

                    {getProductVideo({ renderVideo: true })}
                    {!hideTopLeftBadge && getProductBadge()}
                    <div
                        className={cx('imageDetails', playVideo && 'hideImage', {
                            gridimageDetails: enableGridViewInCertona,
                        })}
                    >
                        {renderProductSliding()}
                        {renderProductImage()}
                        {getProductVideo()}
                        {!hideBottomBadge && getExtendedSize()}
                        {renderQuickAddToCart}
                    </div>
                    {saveForLaterDisplay}
                    {galleryProductCard && !imageLoad && renderSkeletonLoader()}
                </div>
                {/* eslint-disable-next-line no-nested-ternary */}
                {renderSwatches()}
                <div
                    className={`${dt(['pt-0', 'pr-0.5', 'mt-2', 'block', 'relative'])} ${cx({
                        newPriceDetailsWrapper: enableNewPriceFormat,
                    })}`}
                    onFocus={resetSelectionForEnsembleHover}
                    onMouseOver={resetSelectionForEnsembleHover}
                >
                    {renderProductTitle}
                    {!hideBottomBadge ? getExtendedSize({ inDescription: true }) : null}
                    {renderPricing}
                    {ecoRebate ? <EcoRebates skuId={skuId} /> : null}
                    {isCriteoProduct ? (
                        <div className={cx('sponsored')}>Sponsored Product</div>
                    ) : null}
                    {renderRating}
                    <ProductAvailabilityConnected
                        deviceType={deviceType}
                        cardType={cardType}
                        availabilityStatus={availabilityStatus}
                    />
                    {children}
                </div>
            </div>
        );
    };

    return (
        <div
            className={cx('productCard', customClass, cardTheme, {
                couponRecommendations: isCouponRecommendationsModal,
            })}
            ref={productCardRef}
        >
            <div
                className={cx('card', 'cardWrapper', cardCustomClass, {
                    cardMinWidth: isDesktop,
                })}
                data-automation-id="productCard"
            >
                {shallowCard && showPaneLoader ? (
                    <div className={cx('col')}>
                        <div className={cx('row')}>
                            <div className={cx('imageTileLoader')} />
                        </div>
                        <div className={cx('row')}>
                            <div className={cx('loader', 'descLoader')} />
                            <div className={cx('loader', 'descLoader')} />
                            <div className={cx('loader', 'descLoader')} />
                        </div>
                    </div>
                ) : (
                    renderCard()
                )}
            </div>
        </div>
    );
};

ProductCard.defaultProps = {
    altImageUrl: '',
    badge: null,
    bottomBadge: {},
    cardCustomClass: '',
    cardTheme: cardThemes.GALLERY,
    cardType: cardTypes.GRID,
    customClass: '',
    defaultOffSet: 500,
    ecoRebate: false,
    enableMiniPDP: false,
    enableProductTitlePrefix: false,
    imageUrl: '',
    index: 0,
    isAddToCartClick: false,
    isCouponRecommendationsModal: false,
    isCriteoProduct: false,
    isEnsembledProduct: false,
    lazyLoadImages: false,
    noImageFoundSrcDefault: 'https://m.jcpenney.com/b/assets/img/noimage.jpeg',
    onAddToCartClick: () => {},
    onImageClick: () => {},
    onProductClick: () => {},
    ppId: '',
    pricingConfig: {},
    productUrl: '',
    shallowCard: false,
    showPaneLoader: false,
    showPricing: true,
    showSaveForlater: false,
    skuId: '',
    skuSwatch: [],
    title: '',
    upperBadge: {},
    videoId: '',
    enableGridViewInCertona: false,
    displayScene7PictureComponent: false,
    displayS7PictureComponentAltImg: true,
    fromHotDeals: false,
    availabilityStatus: [],
    galleryProductCard: false,
    triggerAnalytics: () => {},
    displayEach: false,
    eligibilityMessage: '',
    price: null,
    rating: 0,
    reviewCount: 0,
    lazyLoadSwatches: false,
    deviceType: { isDesktop: true },
    setImageHeight: () => {},
};

ProductCard.propTypes = {
    altImageUrl: string,
    badge: object,
    bottomBadge: object,
    cardCustomClass: string,
    cardTheme: oneOf([cardThemes.CARTRIDGE, cardThemes.GALLERY, cardThemes.ONE_RECORD_BANNER]),
    cardType: oneOf([cardTypes.GRID, cardTypes.LIST]),
    customClass: string,
    defaultOffSet: number,
    ecoRebate: bool,
    enableMiniPDP: bool,
    enableProductTitlePrefix: bool,
    imageUrl: string,
    index: number,
    isAddToCartClick: bool,
    isCouponRecommendationsModal: bool,
    isCriteoProduct: bool,
    isEnsembledProduct: bool,
    lazyLoadImages: bool,
    noImageFoundSrcDefault: string,
    onAddToCartClick: func,
    onImageClick: func,
    onProductClick: func,
    ppId: string,
    pricingConfig: object,
    productUrl: string,
    shallowCard: bool,
    showPaneLoader: bool,
    showPricing: bool,
    showSaveForlater: bool,
    skuId: string,
    skuSwatch: array,
    title: string,
    upperBadge: object,
    videoId: string,
    enableGridViewInCertona: bool,
    displayScene7PictureComponent: bool,
    displayS7PictureComponentAltImg: bool,
    fromHotDeals: bool,
    availabilityStatus: arrayOf(object),
    galleryProductCard: bool,
    triggerAnalytics: func,
    displayEach: bool,
    eligibilityMessage: string,
    price: object,
    rating: number,
    reviewCount: number,
    lazyLoadSwatches: bool,
    deviceType: object,
    children: node.isRequired,
    setImageHeight: func,
};

export default React.memo(ProductCard);
